import { type ReactNode } from "react";
import { useBrands, type BrandKit } from "@/modules/brand";
import { getTextColorForBackground } from "@/lib/utils";
import { useEditableField } from "./use-editable-field";

interface PreviewLayoutProps {
	id: string;
	brandName: string;
	brandLogo: string | null;
	brandkit: BrandKit;
	/** Small uppercase text above the title (e.g., "QUESTION 1 OF 2") */
	eyebrow?: string;
	title: ReactNode;
	/** Placeholder text to show when title is empty */
	titlePlaceholder?: string;
	subtitle?: ReactNode;
	/** Placeholder text to show when subtitle is empty (clickable to activate editor) */
	subtitlePlaceholder?: string;
	children: ReactNode;
	/** Progress indicator (e.g., { current: 2, total: 5 }) */
	progress?: { current: number; total: number };
	/** Show footer with continue button */
	showFooter?: boolean;
	/** Continue button label */
	continueLabel?: string;
	/** Show back button */
	showBackButton?: boolean;
	/** Center content vertically */
	centered?: boolean;
	/** Make title editable */
	editableTitle?: boolean;
	/** Make subtitle editable */
	editableSubtitle?: boolean;
	/** Callback when title changes (local state only) */
	onLocalTitleChange?: (title: string) => void;
	/** Callback when subtitle changes (local state only) */
	onLocalSubtitleChange?: (subtitle: string) => void;
	/** Enable dark mode */
	isDarkMode?: boolean;
}

export default function PreviewLayout({
	id,
	brandName,
	brandLogo,
	brandkit,
	eyebrow,
	title,
	titlePlaceholder,
	subtitle,
	subtitlePlaceholder,
	children,
	progress,
	showFooter = true,
	continueLabel = "Continue",
	showBackButton = false,
	centered = true,
	editableTitle = false,
	editableSubtitle = false,
	onLocalTitleChange,
	onLocalSubtitleChange,
	isDarkMode = false,
}: PreviewLayoutProps) {
	const { selectedBrand } = useBrands();
	const showBranding = selectedBrand?.branding !== "disabled";

	// Use the editable field hook for title and subtitle
	const { ref: titleRef, handlers: titleHandlers, isEmpty: titleIsEmpty } = useEditableField(typeof title === "string" ? title : "", onLocalTitleChange, titlePlaceholder);
	const { ref: subtitleRef, handlers: subtitleHandlers, isEmpty: subtitleIsEmpty } = useEditableField(typeof subtitle === "string" ? subtitle : "", onLocalSubtitleChange, subtitlePlaceholder);
	return (
		<div id={id} className={`min-h-full flex flex-col overflow-hidden ${isDarkMode ? "bg-gray-950" : "bg-gray-50"}`}>
			{/* Progress bar or accent bar at top */}
			{progress ? (
				<div className={`w-full h-1 shrink-0 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
					<div
						className="h-full transition-all duration-500 ease-out"
						style={{
							width: `${(progress.current / progress.total) * 100}%`,
							backgroundColor: brandkit.color_primary_hex,
						}}
					/>
				</div>
			) : (
				<div
					className="h-1 w-full shrink-0"
					style={{
						backgroundColor: brandkit.color_primary_hex,
					}}
				/>
			)}

			{/* Main container - centered with max width */}
			<div className={`flex-1 flex flex-col w-full max-w-xl mx-auto px-6 md:px-8 ${centered ? "justify-center" : ""}`}>
				{/* Header with logo */}
				<header className={`pt-16 pb-10 shrink-0 ${centered ? "" : "pt-20"}`}>
					{brandLogo ? (
						<img src={brandLogo} alt={brandName} className="h-8 md:h-10 w-auto object-contain" />
					) : (
						<span className={`text-xl font-bold text-primary`} style={{ fontFamily: "var(--font-body, inherit)", fontWeight: "var(--font-body-weight, 600)" }}>
							{brandName}
						</span>
					)}
				</header>

				{/* Content area */}
				<main className="flex-1 flex flex-col">
					{/* Eyebrow */}
					{eyebrow && (
						<p className={`text-xs font-medium uppercase tracking-widest mb-3 ${isDarkMode ? "text-gray-400" : "text-tertiary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
							{eyebrow}
						</p>
					)}

					{/* Title */}
					{editableTitle && typeof title === "string" ? (
						<div className="relative">
							<h1
								ref={titleRef as React.Ref<HTMLHeadingElement>}
								className={`text-[36px] leading-[1.3] tracking-tight outline-none rounded-sm transition-shadow cursor-text ${
									isDarkMode
										? `hover:ring-2 hover:ring-gray-600 focus:ring-2 focus:ring-brand/50 ${titleIsEmpty ? "text-transparent caret-white" : "text-white"}`
										: `hover:ring-2 hover:ring-gray-300 focus:ring-2 focus:ring-brand/50 ${titleIsEmpty ? "text-transparent caret-black" : "text-primary"}`
								}`}
								style={{ fontFamily: "var(--font-heading, inherit)", fontWeight: "var(--font-heading-weight, 600)", minHeight: "1.3em" }}
								contentEditable
								suppressContentEditableWarning
								{...titleHandlers}
							/>
							{titleIsEmpty && titlePlaceholder && (
								<span
									className={`absolute inset-0 text-[36px] leading-[1.3] tracking-tight pointer-events-none whitespace-nowrap ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
									style={{ fontFamily: "var(--font-heading, inherit)", fontWeight: "var(--font-heading-weight, 600)" }}
								>
									{titlePlaceholder}
								</span>
							)}
						</div>
					) : (
						<h1
							className={`text-[36px] leading-[1.3] tracking-tight ${isDarkMode ? "text-white" : "text-primary"}`}
							style={{ fontFamily: "var(--font-heading, inherit)", fontWeight: "var(--font-heading-weight, 600)" }}
						>
							{title}
						</h1>
					)}

					{/* Subtitle */}
					{editableSubtitle && typeof subtitle === "string" ? (
						<div className="relative mt-3">
							<p
								ref={subtitleRef as React.Ref<HTMLParagraphElement>}
								className={`text-base md:text-lg leading-relaxed outline-none rounded-sm transition-shadow cursor-text ${
									isDarkMode
										? `hover:ring-2 hover:ring-gray-600 focus:ring-2 focus:ring-brand/50 ${subtitleIsEmpty ? "text-transparent caret-white" : "text-gray-300"}`
										: `hover:ring-2 hover:ring-gray-300 focus:ring-2 focus:ring-brand/50 ${subtitleIsEmpty ? "text-transparent caret-black" : "text-secondary"}`
								}`}
								style={{ fontFamily: "var(--font-body, inherit)", fontWeight: "var(--font-body-weight, 400)", minHeight: "1.5em" }}
								contentEditable
								suppressContentEditableWarning
								{...subtitleHandlers}
							/>
							{subtitleIsEmpty && subtitlePlaceholder && (
								<span
									className={`absolute inset-0 text-base md:text-lg leading-relaxed pointer-events-none whitespace-nowrap ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
									style={{ fontFamily: "var(--font-body, inherit)", fontWeight: "var(--font-body-weight, 400)" }}
								>
									{subtitlePlaceholder}
								</span>
							)}
						</div>
					) : subtitle ? (
						<p
							className={`mt-3 text-base md:text-lg leading-relaxed ${isDarkMode ? "text-gray-300" : "text-secondary"}`}
							style={{ fontFamily: "var(--font-body, inherit)", fontWeight: "var(--font-body-weight, 400)" }}
						>
							{subtitle}
						</p>
					) : null}

					{/* Main content */}
					<div className="mt-8 md:mt-10">{children}</div>
				</main>
			</div>

			{/* Footer with buttons - sticky style matching BoldLayout */}
			{showFooter && (
				<footer className="shrink-0 mt-auto">
					{/* Fade gradient above toolbar */}
					<div className={`h-16 pointer-events-none ${isDarkMode ? "bg-linear-to-t from-gray-950 to-transparent" : "bg-linear-to-t from-gray-50 to-transparent"}`} />
					<div className={`py-5 md:py-6 ${isDarkMode ? "bg-gray-900 border-t border-gray-800" : "bg-white border-t border-gray-100"}`}>
						<div className="w-full max-w-xl mx-auto px-6 md:px-8">
							{/* Full-width button when no back button and no progress */}
							{!showBackButton && !progress ? (
								<div className="space-y-4">
									{/* Continue button - 3D style */}
									<div
										className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-lg cursor-not-allowed"
										style={{
											backgroundColor: "var(--color-accent, #e17100)",
											boxShadow: "0 3px 0 0 color-mix(in srgb, var(--color-accent, #e17100) 70%, black)",
											fontFamily: "var(--font-body, inherit)",
											color: getTextColorForBackground(brandkit.color_accent_hex),
										}}
									>
										{continueLabel}
										<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
											<path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
										</svg>
									</div>
									{/* Powered by text */}
									{showBranding && (
										<div className="flex justify-center">
											{/* <span className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
												Powered by{" "}
												<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">
													Say About Us
												</a>
											</span> */}

											<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline block hover:opacity-80">
												<img src="/images/badge-sayaboutus-2.svg" alt="build social proof with Say About Us" className="h-7 w-auto" />
											</a>
										</div>
									)}
								</div>
							) : (
								<div className="flex items-center gap-3">
									{/* Back button - 3D outlined style */}
									<div className="w-[120px] shrink-0">
										{showBackButton ? (
											<div
												className={`w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-lg cursor-not-allowed whitespace-nowrap ${
													isDarkMode
														? "text-white bg-gray-800 border border-gray-700 shadow-[0_3px_0_0_#374151]"
														: "text-primary bg-white border border-gray-300 shadow-[0_3px_0_0_#d1d5db]"
												}`}
												style={{ fontFamily: "var(--font-body, inherit)" }}
											>
												<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
													<path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
												</svg>
												Back
											</div>
										) : (
											<div className="w-full" />
										)}
									</div>

									{/* Center branding */}
									<div className="flex-1 flex justify-center">
										{showBranding && (
											// <span className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
											// 	Powered by{" "}
											// 	<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">
											// 		Say About Us
											// 	</a>
											// </span>
											<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline block hover:opacity-80">
												<img src="/images/badge-sayaboutus-2.svg" alt="build social proof with Say About Us" className="h-7 w-auto" />
											</a>
										)}
									</div>

									{/* Continue button - 3D filled style */}
									<div className="w-[120px] shrink-0">
										<div
											className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-lg cursor-not-allowed whitespace-nowrap"
											style={{
												backgroundColor: "var(--color-accent, #e17100)",
												boxShadow: "0 3px 0 0 color-mix(in srgb, var(--color-accent, #e17100) 70%, black)",
												fontFamily: "var(--font-body, inherit)",
												color: getTextColorForBackground(brandkit.color_accent_hex),
											}}
										>
											{continueLabel}
											<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
												<path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
											</svg>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
				</footer>
			)}
		</div>
	);
}
